<script setup lang="ts">
import { computed, watch } from 'vue';
import { useStore } from 'vuex';
import { RcSection } from '@components/RcSection';
import { RadioGroup } from '@components/Form/Radio';
import LabeledSelect from '@shell/components/form/LabeledSelect';
import { RcContentGroup } from '@components/Layout';
import { Banner } from '@components/Banner';
import FleetPolicyAllowList from '@shell/components/fleet/FleetPolicyAllowList.vue';
import { useI18n } from '@shell/composables/useI18n';
import type { FleetPolicyNameOption, FleetPolicySource } from '@shell/types/fleet';

type Variant = 'gitRepo' | 'helmOp';

/**
 * GitRepo and HelmOp policies hold the same three settings under different field names,
 * so one section renders both and resolves the field names from the variant.
 */
const SECRET_FIELDS: Record<Variant, { default: keyof FleetPolicySource, allowed: keyof FleetPolicySource }> = {
  gitRepo: { default: 'defaultClientSecretName', allowed: 'allowedClientSecretNames' },
  helmOp:  { default: 'defaultHelmSecretName', allowed: 'allowedHelmSecretNames' },
};

const props = withDefaults(defineProps<{
  value: FleetPolicySource;
  variant: Variant;
  mode: string;
  serviceAccountOptions?: string[];
  secretOptions?: FleetPolicyNameOption[];
  serviceAccountsRestricted?: boolean;
  allowedServiceAccounts?: string[];
  defaultServiceAccountAllowed?: boolean;
  defaultSecretAllowed?: boolean;
}>(), {
  serviceAccountOptions:        () => [],
  secretOptions:                () => [],
  serviceAccountsRestricted:    false,
  allowedServiceAccounts:       () => [],
  defaultServiceAccountAllowed: true,
  defaultSecretAllowed:         true,
});

const { t } = useI18n(useStore());

const secretFields = computed(() => SECRET_FIELDS[props.variant]);
const prefix = computed(() => `fleet.policy.${ props.variant }`);
const testid = computed(() => `fleet-policy-${ props.variant === 'gitRepo' ? 'git-repo' : 'helm-op' }`);

const setField = (field: keyof FleetPolicySource, value: string | string[]) => {
  (props.value as Record<string, unknown>)[field] = value;
};

const allowedSecrets = computed(() => (props.value[secretFields.value.allowed] as string[]) || []);
const restricted = defineModel<boolean>('restricted', { default: false });

const optionName = (option: FleetPolicyNameOption) => (typeof option === 'string' ? option : option.value);

/**
 * A select in the dashboard has no clear button, so a default that has been set is unset by
 * picking None. Its null value never matches the stored name, so the field falls back to its
 * placeholder once it is chosen.
 */
const withNone = (options: FleetPolicyNameOption[]) => [{ label: t('generic.none'), value: null }, ...options];

const defaultServiceAccount = computed({
  get: () => props.value.defaultServiceAccount || '',
  set: (val: string) => {
    props.value.defaultServiceAccount = val || '';
  }
});

// A name the policy would reject is not worth offering, so once the service accounts are
// restricted the default is chosen from the allowed ones. The service accounts are allowed by the
// policy as a whole, while each source holds its own default.
const defaultServiceAccountOptions = computed(() => {
  if (!props.serviceAccountsRestricted) {
    return withNone(props.serviceAccountOptions);
  }

  const options = [...props.allowedServiceAccounts];
  const current = defaultServiceAccount.value;

  // A default that is no longer allowed stays on the list, so the user can see what has to change
  if (current && !options.includes(current)) {
    options.push(current);
  }

  return withNone(options);
});

const defaultSecret = computed({
  get: () => (props.value[secretFields.value.default] as string) || '',
  set: (val: string) => setField(secretFields.value.default, val || ''),
});

// A name the policy would reject is not worth offering, so once the secrets are restricted the
// default is chosen from the allowed ones - including any allowed name that has no secret yet
const optionFor = (name: string) => props.secretOptions.find((option) => optionName(option) === name) || { label: name, value: name };

const defaultSecretOptions = computed(() => {
  if (!restricted.value) {
    return props.secretOptions;
  }

  const options = allowedSecrets.value.map(optionFor);
  const current = defaultSecret.value;

  // A select shows a value it holds no option for as the bare name, losing the type and user the
  // rest of the list carries, so a default that is no longer allowed keeps its own option
  if (current && !options.some((option) => optionName(option) === current)) {
    options.push(optionFor(current));
  }

  return options;
});

const defaultSecretSelectOptions = computed(() => withNone(defaultSecretOptions.value));

const restrictOptions = computed(() => [
  { value: false, label: t(`${ prefix.value }.restrict.all`) },
  {
    value:       true,
    label:       t(`${ prefix.value }.restrict.selected`),
    description: t(`${ prefix.value }.restrict.description`),
  },
]);

// Fleet reads a list left behind as a restriction, so going back to allowing all has to clear it.
// The names are kept for the rest of the session instead of being thrown away, so changing the
// choice back restores them.
let clearedSecrets: string[] = [];

watch(restricted, (val) => {
  if (!val) {
    clearedSecrets = [...allowedSecrets.value];
    setField(secretFields.value.allowed, []);
  } else if (!allowedSecrets.value.length) {
    setField(secretFields.value.allowed, [...clearedSecrets]);
  }
});

const updateAllowed = (val: string[]) => setField(secretFields.value.allowed, val);

// Secrets are offered as options carrying a label, so a name typed in has to be shaped the same
// way, or the select hands back the option object instead of the name the policy stores
const createOption = (name: string) => ({ label: name, value: name });
</script>

<template>
  <RcSection
    :title="t(`${ prefix }.title`)"
    mode="with-header"
    type="primary"
    expandable
    :data-testid="testid"
  >
    <RcContentGroup>
      <div class="row">
        <div class="col span-6 policy-field">
          <LabeledSelect
            v-model:value="defaultServiceAccount"
            :options="defaultServiceAccountOptions"
            :label="t(`${ prefix }.defaultServiceAccount.label`)"
            :placeholder="t('fleet.policy.placeholder.serviceAccount')"
            :mode="props.mode"
            :taggable="true"
            :searchable="true"
            :clearable="true"
            :create-option="createOption"
            :muted-value="!props.defaultServiceAccountAllowed"
            :data-testid="`${ testid }-default-service-account`"
          />
          <p class="sub-description">
            {{ t(`${ prefix }.defaultServiceAccount.description`) }}
          </p>
        </div>
      </div>
      <Banner
        v-if="!props.defaultServiceAccountAllowed"
        color="warning"
        :label="t('fleet.policy.defaultServiceAccountNotAllowed')"
        :data-testid="`${ testid }-default-service-account-not-allowed`"
      />
    </RcContentGroup>
    <RcContentGroup>
      <RadioGroup
        v-model:value="restricted"
        :name="`${ testid }-restrict-secrets`"
        :options="restrictOptions"
        :mode="props.mode"
        :aria-label="t(`${ prefix }.title`)"
        :data-testid="`${ testid }-restrict-secrets`"
      />
      <FleetPolicyAllowList
        v-if="restricted"
        :value="allowedSecrets"
        :title="t(`${ prefix }.allowed.title`)"
        :label="t(`${ prefix }.allowed.label`)"
        :options="props.secretOptions"
        :mode="props.mode"
        :data-testid="`${ testid }-allowed-secrets`"
        @update:value="updateAllowed"
      />
      <div class="row">
        <div class="col span-6 policy-field">
          <LabeledSelect
            v-model:value="defaultSecret"
            :options="defaultSecretSelectOptions"
            :label="t(`${ prefix }.defaultSecret.label`)"
            :placeholder="t('fleet.policy.placeholder.secret')"
            :mode="props.mode"
            :taggable="true"
            :searchable="true"
            :clearable="true"
            :create-option="createOption"
            :muted-value="!props.defaultSecretAllowed"
            :data-testid="`${ testid }-default-secret`"
          />
          <p class="sub-description">
            {{ t(`${ prefix }.defaultSecret.description`) }}
          </p>
        </div>
      </div>
      <Banner
        v-if="!props.defaultSecretAllowed"
        color="warning"
        :label="t('fleet.policy.defaultSecretNotAllowed')"
        :data-testid="`${ testid }-default-secret-not-allowed`"
      />
    </RcContentGroup>
  </RcSection>
</template>

<style lang="scss" scoped>
// A banner carries 15px of its own above and below, which doubles up with the gap the content
// group already puts between its children
.rc-content-group > .banner {
  margin: 0;
}

.policy-field {
  display: flex;
  flex-direction: column;
  gap: 8px;

  .sub-description {
    color: var(--deemphasized);
    font-size: 12px;
  }
}
</style>
